import { API_BASE_URL } from '@/config/constant';
import { MAINTENANCE_CONFIG } from '@/config/maintenance';

export interface HealthCheckResult {
  isHealthy: boolean;
  status?: number;
}

// In-Flight Promise 싱글톤 (동시 다발적 에러 발생 시 단 1회의 헬스체크만 전송하여 서버 부하 방지)
let inFlightHealthCheckPromise: Promise<HealthCheckResult> | null = null;

/**
 * 서버 상태(Health Check) 확인 서비스
 *
 * 보안 및 안정성:
 * 1. 인증 토큰(Authorization)을 일절 포함하지 않는 순수 공개 요청으로 전송 (토큰 유출 방지)
 * 2. 3초 짧은 타임아웃(AbortController) 적용 (지연 시 빠른 점검 화면 판단)
 * 3. In-flight 요청 중복 제거로 클라이언트발 서버 과부하(DoS) 방어
 */
export const checkServerHealth = async (): Promise<HealthCheckResult> => {
  // 이미 진행 중인 헬스체크가 있으면 기존 Promise 공유
  if (inFlightHealthCheckPromise) {
    return inFlightHealthCheckPromise;
  }

  inFlightHealthCheckPromise = (async (): Promise<HealthCheckResult> => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort();
    }, MAINTENANCE_CONFIG.HEALTH_CHECK_TIMEOUT_MS);

    try {
      const url = `${API_BASE_URL}${MAINTENANCE_CONFIG.HEALTH_CHECK_PATH}`;

      const response = await fetch(url, {
        method: 'GET',
        signal: controller.signal,
        cache: 'no-store',
        headers: {
          Accept: 'text/plain, application/json, */*',
        },
      });

      clearTimeout(timeoutId);

      // 서버 스펙: 200 OK일 때만 정상, 200 이외는 비정상(장애)
      const isHealthy = response.status === 200;
      return {
        isHealthy,
        status: response.status,
      };
    } catch {
      clearTimeout(timeoutId);
      // 타임아웃, 네트워크 실패, 서버 다운 등 모든 예외는 비정상(false)으로 간주
      return {
        isHealthy: false,
      };
    } finally {
      inFlightHealthCheckPromise = null;
    }
  })();

  return inFlightHealthCheckPromise;
};
